// Çip filtresi aktifken bölümler-arası DÜZ liste — Geçmiş Rapor Aktarımı (Task 15, 2A).
//
// 2A: çip ≠ "Tümü" iken `activeSectionId` filtresi devre dışı kalır; eşleşen sorular
// hangi bölümde olursa olsun tek akışta görünür. Her kartın üstünde mono
// `{sectionCode} · {sectionLabel}` satırı (aynı bölümün ardışık kartlarında
// tekrarlanmaz), üstte kalıcı `filterHeader` (`role=status aria-live`) +
// "Filtreyi temizle". Eşleşme yoksa `filterEmpty`.
//
// Kart render'ı DIŞARIDAN gelir (`renderQuestion`) — QuestionCard FormFill.tsx'te
// tanımlı ve store'a bağlı; bu bileşen yalnız düzenden sorumludur.

import type { QuestionSnapshot, SectionSnapshot } from "@mahir/shared";
import { fillTemplate } from "../../lib/period-ui";
import { IMPORT_COPY, type ImportQuestionFilter } from "../../lib/report-import-ui";

export interface ProvenanceFilterListProps {
  filter: Exclude<ImportQuestionFilter, "all">;
  /** Filtreye uyan sorular — snapshot sırasında (bölüm order → soru order). */
  questions: QuestionSnapshot[];
  sections: SectionSnapshot[];
  onClearFilter: () => void;
  /** 2A — düzenlenen kart blur'a kadar listede kalsın diye odak izleme. */
  onPin: (questionCode: string | null) => void;
  renderQuestion: (question: QuestionSnapshot) => JSX.Element;
}

const FILTER_LABELS: Record<Exclude<ImportQuestionFilter, "all">, string> = {
  low: IMPORT_COPY.chipLowLabel,
  partial: IMPORT_COPY.chipPartialLabel,
  rejected: IMPORT_COPY.chipRejectedLabel,
  empty: IMPORT_COPY.chipEmptyLabel,
};

export function ProvenanceFilterList(props: ProvenanceFilterListProps): JSX.Element {
  const { filter, questions, sections, onClearFilter, onPin, renderQuestion } = props;
  const sectionById = new Map(sections.map((s) => [s.id, s]));
  const distinctSections = new Set(questions.map((q) => q.sectionId));

  return (
    <div>
      <div
        role="status"
        aria-live="polite"
        className="flex items-center justify-between gap-3 mb-3"
        style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }}
      >
        <span>
          {fillTemplate(IMPORT_COPY.filterHeader, {
            label: FILTER_LABELS[filter],
            n: questions.length,
            s: distinctSections.size,
          })}
        </span>
        <button
          type="button"
          onClick={onClearFilter}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "var(--color-primary)",
            textDecoration: "underline",
            cursor: "pointer",
            font: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {IMPORT_COPY.clearFilter}
        </button>
      </div>

      {questions.length === 0 ? (
        <p style={{ color: "var(--color-text-2)", fontSize: "0.82rem" }}>
          {IMPORT_COPY.filterEmpty}
        </p>
      ) : (
        questions.map((q, i) => {
          const section = sectionById.get(q.sectionId);
          const showSectionRow = i === 0 || questions[i - 1]!.sectionId !== q.sectionId;
          return (
            <div
              key={q.id}
              data-section-group={q.sectionId}
              onFocusCapture={() => onPin(q.code)}
              onBlurCapture={(e) => {
                // Odak kartın dışına çıktıysa pin düşer (2A "blur'a kadar").
                if (!e.currentTarget.contains(e.relatedTarget)) onPin(null);
              }}
            >
              {showSectionRow && section ? (
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "0.7rem",
                    color: "var(--color-text-2)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  {section.code} · {section.label}
                </div>
              ) : null}
              {renderQuestion(q)}
            </div>
          );
        })
      )}
    </div>
  );
}
